/**
 * Mapping of size-based URL slugs to their corresponding sqft range value
 * (or `onlyLocker: true`) used by the Unit Sizes radio in SearchFiltersVariation.
 *
 * Values match storageSizeOptions in config/units.js.
 */
export const SIZE_SLUG_TO_SQFT = {
	'mini-self-storage': { sqft: '0-25' },
	'small-self-storage-units': { sqft: '26-75' },
	'medium-self-storage-units': { sqft: '76-200' },
	'large-self-storage-units': { sqft: '201-999' },
	'self-storage-lockers': { onlyLocker: true },
};

/**
 * Returns the sqft (or onlyLocker) filter params for a given size slug,
 * or null if the slug does not map to a size filter.
 *
 * @param {string} sizeSlug - e.g. "mini-self-storage"
 * @returns {{ sqft: string }|{ onlyLocker: boolean }|null}
 */
export function getSqftParamsForSizeSlug(sizeSlug) {
	return SIZE_SLUG_TO_SQFT[sizeSlug] || null;
}

/**
 * Mapping of amenity URL slugs (used in ssdr ALP routes) to their
 * corresponding filter values in the `amenities` query-param array.
 *
 * Only includes slugs that correspond to a checkbox in the amenities
 * filter group. Size-based slugs (mini-self-storage, etc.) are handled
 * separately by the Unit Sizes radio via SIZE_SLUG_TO_SQFT above.
 */
const AMENITY_SLUG_TO_FILTER = {
	// CLP slugs
	'climate-controlled': 'climateControlled',
	'24-hour-access': 'twentyFourHourAccess',
	'drive-up-self-storage': 'driveUp',
	'free-truck-rental': 'freeTruckRental',
	electricity: 'power',
	// NMP slugs (where they differ from CLP)
	'24-hour': 'twentyFourHourAccess',
	'drive-up': 'driveUp',
};

/**
 * Extracts the amenity slug from a URL pathname.
 *
 * @param {string} pathname - e.g. "/self-storage/texas/austin/drive-up-self-storage"
 * @returns {string|null} The slug (e.g. "drive-up-self-storage") or null
 */
export function parseAmenityFromPath(pathname) {
	if (!pathname || typeof pathname !== 'string') {
		return null;
	}

	const slugs = Object.keys(AMENITY_SLUG_TO_FILTER).join('|');
	const pattern = new RegExp(`\\/(${slugs})\\/?$`, 'i');
	const match = pathname.match(pattern);

	return match ? match[1] : null;
}

/**
 * Returns the amenities-array filter value for a given amenity slug.
 *
 * @param {string} amenitySlug - e.g. "drive-up-self-storage"
 * @returns {string|null} The filter value (e.g. "driveUp") or null
 */
export function getFilterValueForAmenity(amenitySlug) {
	return AMENITY_SLUG_TO_FILTER[amenitySlug] || null;
}
